import React, {
  useCallback,
  useRef,
  useMemo,
  useContext,
  MouseEventHandler,
  startTransition,
  useEffect,
} from 'react';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { URLS } from '@wix/da-url';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import { BiData, TopNavClickBiEvent } from '@wix/da-bi/pkg/events';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import IconButtonWithNotification from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import { useDropdownHover } from '../../hooks/useDropdownHover';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { DamzContext } from '@wix/da-react-context/pkg/DamzContext';
import cn from 'classnames';
import { useRestrictToLoggedInAndVerified } from '../../../utils/hooks/useRestrictToLoggedInAndVerified';
import { ReferralType } from '@wix/da-bi/pkg/constants';

import s from './DamzDropdownWrapper.scss';

export interface Props {
  unreadCount: number;
  className?: string;
  iconClassName?: string;
  numberClassName?: string;
  biData?: BiEvent;
  isDamzPageOpen?: boolean;
  isDamzDropdownOpen: boolean;
  ['aria-label']?: string;
  openDamzDropdown: () => void;
  closeDamzDropdown: () => void;
  toggleDamzPage: () => void;
  toggleBurgerMenu: (open: boolean) => void;
  unreadRequestsCount: number;
}

const DamzDropdownWrapper: React.FC<Props> = ({
  unreadCount,
  className,
  iconClassName,
  numberClassName,
  openDamzDropdown,
  closeDamzDropdown,
  isDamzPageOpen,
  isDamzDropdownOpen,
  toggleDamzPage,
  toggleBurgerMenu,
  unreadRequestsCount,
  'aria-label': ariaLabel,
}) => {
  const { isLoggedIn, isVerificationNeeded, restrictToLoggedInAndVerified } =
    useRestrictToLoggedInAndVerified();

  const { t } = useTranslation();
  const { DrawerComponent, isStandalonePage } = useContext(DamzContext);
  const { handleDropdownHover, handleDropdownLeave } = useDropdownHover({
    isDropdownOpen: isDamzDropdownOpen,
    openDropdown: openDamzDropdown,
    closeDropdown: closeDamzDropdown,
  });

  // no point to spamming this if our guest has twitchy hands
  const throttledMouseLeave = useMemo(
    () => throttle(handleDropdownLeave, 300),
    [handleDropdownLeave]
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const onClose = useCallback(() => {
    throttledMouseLeave();
  }, [throttledMouseLeave]);
  const handleClickOutside = useCallback(() => {
    if (!isDamzDropdownOpen) {
      return;
    }
    onClose();
  }, [isDamzDropdownOpen, onClose]);
  useOnOutsideClick(rootRef, handleClickOutside, true, (target, e) => {
    // find closest div with id like popper-portal-95
    const popperPortal = target.closest('[id^="popper-portal-"]');
    if (popperPortal) {
      return false;
    }
    return true;
  });

  const handleIconClick: MouseEventHandler = useCallback(
    e => {
      // Close dropdown if it's open
      if (isDamzDropdownOpen) {
        closeDamzDropdown();
      }

      if (!isStandalonePage) {
        // We can use startTransition outright. useTransition's pending state is unnecessary since
        // a loading screen gets mounted immediately.
        startTransition(() => {
          yieldToMain(() => {
            toggleBurgerMenu(false); // Close network bar, if it's open
            toggleDamzPage();
          });
        });
      }

      // We put an `href` attribute on the element that calls this so users can open notifications in a new tab,
      // so we prevent default to avoid the standard handling of an anchor tag click since we're duper browsing.
      e.preventDefault();
    },
    [
      isDamzDropdownOpen,
      closeDamzDropdown,
      isStandalonePage,
      toggleDamzPage,
      toggleBurgerMenu,
    ]
  );

  const restrictedHandleIconClick = restrictToLoggedInAndVerified(
    ReferralType.HEADER,
    undefined,
    handleIconClick
  );

  useEffect(() => {
    if (isDamzPageOpen && isDamzDropdownOpen) {
      closeDamzDropdown();
    }
  }, [isDamzPageOpen, isDamzDropdownOpen, closeDamzDropdown]);

  return (
    <div
      className={className}
      onMouseEnter={() => {
        if (
          !isDamzPageOpen &&
          !isStandalonePage &&
          isLoggedIn &&
          !isVerificationNeeded
        ) {
          handleDropdownHover();
        }
      }}
      onMouseLeave={() => handleDropdownLeave()}
      ref={rootRef}
    >
      <IconButtonWithNotification
        className={s['above-dropdown']}
        icon={MailIcon}
        size="medium"
        notificationColor="red"
        iconButtonClassName={iconClassName}
        numberClassName={cn(
          numberClassName,
          unreadRequestsCount > 0 && unreadCount === 0 && s['micro-dot']
        )}
        count={unreadCount}
        biData={BiData<TopNavClickBiEvent>({
          evid: 700,
          link_name: 'damz',
          indicator: unreadCount,
        })}
        href={isStandalonePage || isDamzPageOpen ? undefined : URLS.MESSAGES}
        onClick={restrictedHandleIconClick}
        aria-label={t('siteHeader.a11y.messages.withItems', {
          formattedCount: unreadCount,
        })}
      />
      {DrawerComponent && !isStandalonePage && (
        <div role="dialog" id="site-header-damz" className={s['wrapper']}>
          {isDamzDropdownOpen && (
            <div className={cn(s['container'], s['active'])}>
              <div className="ds-surface-tertiary">
                <DrawerComponent
                  isDropdown
                  openDamzPage={() => toggleDamzPage && toggleDamzPage()}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
DamzDropdownWrapper.displayName = 'DamzDropdownWrapper';

export default DamzDropdownWrapper;
